import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useAnalytics } from "connected-components/analytics"
import { log } from "helpers/logger"
import { omit } from "lodash"
import { Creators } from "model/apiWrapper"
import { useDispatch } from "react-redux"
import { v4 } from "uuid"
import { AI_TEAMMATE_EVENTS } from "../analytics"
import { ERROR_CODES, MANAGED_AGENT_TYPE, AICHAT_AGENT_TYPE } from "../helpers/constants"
import { RefreshableError, RetryableError } from "../helpers/errors"
import { addMessagesToCache, createOptimisticMessage, getMessagesQueryKey, removeOptimisticMessages } from "../utils/message-cache"
import { useAiTeammateContext, useThreadsContext } from "./context"
import { addThreadToCache, getThreadsQueryKey } from "../utils/threads-cache"

// Builds the chat send payload. The legacy (portfolio-scoped) assistant sends agentArguments built from
// the active instrument/model; the managed agent has no portfolio context, so it sends only the
// selected risk model (chosen from the header model selector). op-summarize requires model_id to be
// present but routes the managed agent via the thread's stored/created config, ignoring the value.
export function buildSendMessagePayload({ messageText, isManagedAgent, agentContext, threadId, lastAssistantMessageId }) {
  const payload = { message: messageText }

  if (!isManagedAgent) {
    const { modelId, resourceMetadata, instrument, base, startDate, endDate } = agentContext
    payload.agentArguments = {
      modelId,
      positionSet: {
        id: resourceMetadata?.alias || instrument.id,
        type: instrument.type,
      },
      base,
      startDate,
      endDate,
    }
  } else {
    payload.agentArguments = { modelId: agentContext.modelId }
  }

  // Tag the originating surface's agentType on the thread-start request so pi-server records it.
  // Continuing a thread keeps the stored type, so only the start request (no threadId) carries it.
  if (!threadId) {
    payload.agentArguments.agentType = isManagedAgent ? MANAGED_AGENT_TYPE : AICHAT_AGENT_TYPE
  }

  if (threadId) {
    payload.lastAssistantMsgId = lastAssistantMessageId
  }

  return payload
}

export default function useSendMessage() {
  const analytics = useAnalytics()
  const { modelId, resourceMetadata, instrument, base, startDate, endDate, setError, isManagedAgent } = useAiTeammateContext()
  const { openThread } = useThreadsContext()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  async function handleMutate({ messageText, accountAlias, threadId }) {
    const queryKey = getMessagesQueryKey(accountAlias, threadId)
    await queryClient.cancelQueries({ queryKey, exact: true })
    const messagesCacheSnapshot = queryClient.getQueryData(queryKey)

    const optimisticUserMessage = createOptimisticMessage(messageText, v4())
    addMessagesToCache(queryClient, queryKey, [optimisticUserMessage])
    setError(null)

    return { messagesCacheSnapshot, optimisticUserMessage }
  }

  function handleSuccess(data, { threadId, accountAlias }, context) {
    const { optimisticUserMessage } = context
    const { previousMessageId: userMessageId, assistantMessage, threadId: newThreadId } = data
    const realMessages = [{
      ...omit(optimisticUserMessage, "isOptimistic"),
      id: userMessageId,
    }, assistantMessage]

    const originalQueryKey = getMessagesQueryKey(accountAlias, threadId)
    removeOptimisticMessages(queryClient, originalQueryKey)

    if (!threadId) {
      const threadsQueryKey = getThreadsQueryKey(accountAlias)
      const title = realMessages[0]?.text || "New Chat"
      addThreadToCache(queryClient, threadsQueryKey, {
        threadId: newThreadId,
        title,
        updatedAt: new Date().toISOString(),
        agentType: isManagedAgent ? MANAGED_AGENT_TYPE : AICHAT_AGENT_TYPE,
      })
      openThread(newThreadId, title)
      analytics.track(AI_TEAMMATE_EVENTS.NEW_THREAD_CREATED, { threadId: newThreadId })
    }

    // Add the real messages to the cache
    const newQueryKey = getMessagesQueryKey(accountAlias, newThreadId)
    addMessagesToCache(queryClient, newQueryKey, realMessages)
  }

  function handleError(err, { accountAlias, threadId }, context) {
    const snapshot = context?.messagesCacheSnapshot
    if (snapshot) {
      queryClient.setQueryData(getMessagesQueryKey(accountAlias, threadId), snapshot)
    }

    if (err.error?.code === ERROR_CODES.CHAT_CONTINUE_THREAD_WITH_MESSAGE_CONFLICT) {
      setError(new RefreshableError(
        "This message is out of sync with the current thread. Please refresh the thread and try again.",
      ))
    } else {
      setError(new RetryableError(
        "Oops! Something went wrong on my end. Please try again.",
      ))
    }

    log(err)
  }

  function handleSettled(_, err, { messageText }) {
    analytics.track(AI_TEAMMATE_EVENTS.MESSAGE_SENT, { messageText, err })
  }

  return useMutation({
    mutationFn: ({ messageText, accountAlias, threadId, lastAssistantMessageId }) => {
      const payload = buildSendMessagePayload({
        messageText,
        isManagedAgent,
        agentContext: { modelId, resourceMetadata, instrument, base, startDate, endDate },
        threadId,
        lastAssistantMessageId,
      })

      return new Promise((resolve, reject) => {
        let apiUrl = `/api/${accountAlias}/ai/chat/threads`
        if (threadId) {
          apiUrl += `/${threadId}`
        }
        dispatch(Creators.post(apiUrl, payload, resolve, reject))
      })
    },
    onMutate: handleMutate,
    onSuccess: handleSuccess,
    onError: handleError,
    onSettled: handleSettled,
  })
}
